import React from 'react';
import {
  Grid,
  ISelectOption,
  NumberInputStyled,
  FormCell,
  SpruceField,
  ISpruceFieldProps,
  SelectField,
  MultipleSelectField,
  TextField,
  getColoredCurativeGradeOptions,
  getCurativeGradeDot,
} from 'ui-toolkit';
import { NumberFormatValues } from 'react-number-format';
import styled from 'styled-components';
import { MultiDateField } from '../../ui-toolkit/components/MultiDateInput/MultiDateField';
import { eOrderGroupCategory } from '../../ui-toolkit/enums/eOrderGroupCategory';
import { ITagData } from '../../types/tags/ITagData';
import { eUSAState, StatesLabelValue } from '../../ui-toolkit/enums/states';
import { DisplayTransactionCategory, eTransactionCategory } from '../../ui-toolkit/enums/eTransactionType';
import { eInternalOrderStatus, prettyInternalOrderStatus } from '../../ui-toolkit/enums/eInternalOrderStatus';
import { DefaultOrdersSearchForm } from '../../ui-toolkit/orders/searchOrder/components/DefaultOrdersSearchForm';
import { ICountyData } from '../../ui-toolkit/orders/searchOrder/types/ICounty';
import { labelComparator } from '../../utilities/labelComparator';
import { eServices, DisplayServices } from '../../ui-toolkit/enums/eServices';

interface IAdvancedOrdersSearchFormProps {
  admins: ISelectOption[];
  lenders: ISelectOption[];
  clients: ISelectOption[];
  tags: ITagData[];
  counties: ICountyData[];
  onStateSelect: (state: eUSAState | null) => void;
}

export const AdvancedOrdersSearchForm: React.FC<IAdvancedOrdersSearchFormProps> = ({
  admins,
  lenders,
  clients,
  counties,
  tags,
  onStateSelect,
}) => {
  lenders.sort(labelComparator);
  clients.sort(labelComparator);
  const statusOptions: ISelectOption[] = Object.values(eInternalOrderStatus).map((status) => ({
    label: prettyInternalOrderStatus[status],
    value: status,
  }));
  const stateFilterOptions = (option: ISelectOption<string>, value: string) => {
    return option.value.toLowerCase().indexOf((value || '').toLowerCase()) >= 0;
  };
  const countiesOptions: ISelectOption[] = counties
    .map((county) => ({
      label: county.name,
      value: county.id,
    }))
    .sort(labelComparator);
  const transactionCategoryOptions: ISelectOption[] = Object.values(eTransactionCategory).map((category) => ({
    label: DisplayTransactionCategory[category],
    value: category,
  }));

  const orderGroupOptions: ISelectOption[] = Object.keys(eOrderGroupCategory).map((group) => ({
    label: group,
    value: eOrderGroupCategory[group],
  }));

  const tagsOptions: ISelectOption[] = tags.map((tag) => ({
    label: tag.label,
    value: tag.id,
  }));

  const curativeGradeOptions: ISelectOption[] = getColoredCurativeGradeOptions();

  const servicesOptions: ISelectOption[] = Object.values(eServices).map((service) => ({
    label: DisplayServices[service],
    value: service,
  }));

  return (
    <Grid>
      <DefaultOrdersSearchForm />
      <GridAreas>
        <GridFormItem gridArea={'city'}>
          <TextField name="city" placeholder="Search for a city" label="City" />
        </GridFormItem>
        <GridFormItem gridArea={'state'}>
          <SelectField
            name={'state'}
            onChange={(value) => onStateSelect(value ?? null)}
            filterOption={stateFilterOptions}
            options={StatesLabelValue}
            isClearable
            label={'State'}
            placeholder={'Select a State'}
          />
        </GridFormItem>
        <GridFormItem gridArea={'county'}>
          <SelectField
            name="countyId"
            options={countiesOptions}
            placeholder="Select a county"
            label="County"
            isClearable
          />
        </GridFormItem>
        <GridFormItem gridArea={'zipcode'}>
          <SpruceField name={'zipCode'}>
            {({ field, formik }: ISpruceFieldProps) => (
              <FormCell errorText={field.errorText} label={`Zip Code`}>
                <NumberInputStyled
                  errorText={field.errorText}
                  showErrorHighlight={!!field.errorText}
                  allowEmptyFormatting={false}
                  value={field.value || undefined}
                  format={`#####`}
                  type={'tel'}
                  placeholder={`12345`}
                  onBlur={() => formik.setFieldTouched(field.name)}
                  onValueChange={({ value }: NumberFormatValues) => {
                    formik.setFieldValue(field.name, value ? value : null);
                  }}
                />
              </FormCell>
            )}
          </SpruceField>
        </GridFormItem>
        <GridFormItem gridArea={'lender'}>
          <MultipleSelectField
            name="lenderCompanyIds"
            label={'Lenders'}
            options={lenders}
            isClearable
            placeholder={'Search for Lenders'}
          />
        </GridFormItem>
        <GridFormItem gridArea={'client'}>
          <MultipleSelectField
            name="clientCompanyIds"
            options={clients}
            isClearable
            label={'Clients'}
            placeholder={'Search for Clients'}
          />
        </GridFormItem>
      </GridAreas>
      <Grid columnCount={5}>
        <TextField name="loanNumber" label={'Loan ID'} placeholder={'Search by Loan ID'} />
        <SelectField
          name={'orderGroupCategory'}
          label={'Order Type'}
          options={orderGroupOptions}
          isClearable
          placeholder={'Select'}
        />
        <TextField name={`groupId`} label={`Group ID`} placeholder={`Search by Bulk or Mobility ID`} />
        <TextField
          name={'externalPartyTransactionId'}
          label={'External Transaction ID'}
          placeholder={'Search by Transaction ID'}
        />
        <SelectField
          name={'transactionCategory'}
          label={'Transaction Category'}
          options={transactionCategoryOptions}
          isClearable
          placeholder={'Select'}
        />
      </Grid>
      <Grid columnCount={5}>
        <MultipleSelectField
          name={'statuses'}
          options={statusOptions}
          isClearable
          label={'Status(es)'}
          placeholder={'Select Status(es)'}
        />
        <MultipleSelectField
          name={'services'}
          options={servicesOptions}
          isClearable
          label={'Services'}
          placeholder={'Select Services'}
        />
        <SelectField name={'tag'} label={'Order Tags'} options={tagsOptions} isClearable placeholder={'Select a tag'} />
      </Grid>
      <Grid columnCount={4}>
        <MultipleSelectField
          name={'curativeGrades'}
          isClearable
          options={curativeGradeOptions}
          label={'Curative Grade(s)'}
          placeholder={'Curative Grade(s)'}
          styles={{
            option: (baseStyles, { data }) => ({
              ...baseStyles,
              ...getCurativeGradeDot(data),
            }),
          }}
        />
        <SelectField name="internalContactAdminId" options={admins} label="Internal Contact" isClearable />
      </Grid>
      <Grid columnCount={3}>
        <MultiDateField
          label={'Created At'}
          startName={'createdAtStart'}
          endName={'createdAtEnd'}
          startDate={null}
          endDate={null}
        />
        <MultiDateField
          label={'Closing Date'}
          startName={'closingDateStart'}
          endName={'closingDateEnd'}
          startDate={null}
          endDate={null}
        />
      </Grid>
    </Grid>
  );
};

const GridAreas = styled(Grid)`
  @media (max-width: 1679px) {
    grid-template-areas:
      'city state county zipcode'
      'lender lender client client';
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: repeat(2, 1fr);
  }
  grid-template-areas: 'city state county zipcode lender client';
  grid-template-columns: repeat(6, 1fr);
`;

const GridFormItem = styled.div<{ gridArea: string }>`
  grid-area: ${(props) => props.gridArea};
`;
